import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/lib/i18n';
import { ThumbsUp, ThumbsDown, MessageSquare, Loader2, Building2 } from 'lucide-react';

function normalizeFeedback(payload) {
  const items = Array.isArray(payload) ? payload : (payload?.items || []);
  const up = typeof payload?.up === 'number' ? payload.up : items.filter((it) => it.rating === 'up').length;
  const down = typeof payload?.down === 'number' ? payload.down : items.filter((it) => it.rating === 'down').length;
  const total = typeof payload?.total === 'number' ? payload.total : items.length;

  return { items, up, down, total };
}

function formatDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('de-DE');
  } catch {
    return value;
  }
}

function shortId(value) {
  if (!value) return '—';
  return `${String(value).slice(0, 8)}…`;
}

export default function FeedbackPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isSuperadmin = user?.role === 'superadmin';
  const tenantId = user?.tenant_id;

  const [data, setData] = useState({ items: [], up: 0, down: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const endpoint = isSuperadmin
      ? '/admin/feedback'
      : tenantId
        ? `/tenants/${tenantId}/feedback`
        : null;

    if (!endpoint) {
      setData({ items: [], up: 0, down: 0, total: 0 });
      setLoading(false);
      return;
    }

    setLoading(true);
    api.get(endpoint)
      .then((res) => setData(normalizeFeedback(res.data)))
      .catch(() => setData({ items: [], up: 0, down: 0, total: 0 }))
      .finally(() => setLoading(false));
  }, [isSuperadmin, tenantId]);

  const title = isSuperadmin ? (t.globalFeedback || 'Globales Feedback') : t.pilotFeedback;
  const subtitle = isSuperadmin
    ? (t.globalFeedbackSubtitle || 'Widget-Feedback über alle Mandanten hinweg')
    : t.feedbackSubtitle;

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid={isSuperadmin ? 'global-feedback-page' : 'feedback-page'}>
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-[#EF694C]" /> {title}
        </h1>
        <p className="text-sm text-[#a1a1aa] mt-1">{subtitle}</p>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-[#121214] border border-white/10 rounded-xl p-4" data-testid="feedback-stat-total">
          <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">{t.feedbackTotal}</p>
          <p className="text-2xl font-bold text-white">{data.total}</p>
        </div>
        <div className="bg-[#121214] border border-white/10 rounded-xl p-4" data-testid="feedback-stat-up">
          <p className="text-xs text-[#10B981] uppercase tracking-wider mb-1 flex items-center gap-1">
            <ThumbsUp className="w-3 h-3" /> {t.feedbackUp}
          </p>
          <p className="text-2xl font-bold text-white">{data.up}</p>
        </div>
        <div className="bg-[#121214] border border-white/10 rounded-xl p-4" data-testid="feedback-stat-down">
          <p className="text-xs text-[#F43F5E] uppercase tracking-wider mb-1 flex items-center gap-1">
            <ThumbsDown className="w-3 h-3" /> {t.feedbackDown}
          </p>
          <p className="text-2xl font-bold text-white">{data.down}</p>
        </div>
      </div>

      <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="w-5 h-5 text-[#EF694C] animate-spin" />
          </div>
        ) : data.items.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#a1a1aa]" data-testid="feedback-empty">
            {t.feedbackEmpty}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="feedback-table">
              <thead className="bg-white/5">
                <tr className="text-left text-xs text-[#a1a1aa] uppercase tracking-wider">
                  <th className="px-4 py-3">{t.feedbackRating}</th>
                  {isSuperadmin && <th className="px-4 py-3">{t.feedbackTenant || 'Mandant'}</th>}
                  <th className="px-4 py-3">{t.feedbackComment}</th>
                  <th className="px-4 py-3">{t.feedbackConversation}</th>
                  <th className="px-4 py-3">{t.feedbackDate}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => (
                  <tr key={it.id || `${it.conversation_id}-${it.created_at}`} className="border-t border-white/5" data-testid={`feedback-row-${it.id || it.conversation_id}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {it.rating === 'up' ? (
                        <span className="inline-flex items-center gap-1 text-[#10B981]">
                          <ThumbsUp className="w-3.5 h-3.5" /> {t.feedbackHelpful}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#F43F5E]">
                          <ThumbsDown className="w-3.5 h-3.5" /> {t.feedbackNotHelpful}
                        </span>
                      )}
                    </td>

                    {isSuperadmin && (
                      <td className="px-4 py-3 text-[#d4d4d8] whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5 text-[#52525b]" />
                          {it.tenant_name || it.company_name || shortId(it.tenant_id)}
                        </span>
                      </td>
                    )}

                    <td className="px-4 py-3 text-[#d4d4d8] max-w-md">
                      {it.comment ? <span className="line-clamp-2">{it.comment}</span> : <span className="text-[#52525b]">—</span>}
                    </td>

                    <td className="px-4 py-3 font-mono text-xs text-[#a1a1aa]">
                      {shortId(it.conversation_id)}
                      {it.message_id && <span className="block text-[10px] text-[#52525b]">msg {shortId(it.message_id)}</span>}
                    </td>

                    <td className="px-4 py-3 text-xs text-[#a1a1aa] whitespace-nowrap">
                      {formatDate(it.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
